/**
 * Remembers how wide the teacher dragged the read-aloud modal.
 *
 * Lives in its own module so it can be unit-tested without importing
 * ReadAloudModal, which pulls in framer-motion and a `document`-bound portal.
 */

export const READ_ALOUD_MODAL_WIDTH_KEY = 'curipod_read_aloud_modal_width';

/**
 * Widths are the *visible* outer width of the white modal card, in px.
 *
 * 640 is what the modal measured before it became resizable (a 600px content
 * box inside a 20px white frame), so a teacher who never touches the handle
 * sees exactly what they saw before.
 */
export const DEFAULT_READ_ALOUD_MODAL_WIDTH = 640;
export const MIN_READ_ALOUD_MODAL_WIDTH = 360;

/**
 * Space kept free either side of the card so the floating controls stay on
 * screen at full width: the font-size / voice panel hangs 65px off the left
 * edge and the close button reaches 105px past the right.
 */
const VIEWPORT_GUTTER = 110;

/**
 * Fallback gutter for viewports too narrow to honour the one above. There's no
 * room for the floating controls on a phone either way, so the goal drops to
 * simply keeping the card itself on screen.
 */
const NARROW_VIEWPORT_GUTTER = 16;

export function clampReadAloudModalWidth(width: number, viewportWidth?: number): number {
  const requested = Number.isFinite(width) ? width : DEFAULT_READ_ALOUD_MODAL_WIDTH;

  const viewport =
    viewportWidth ?? (typeof window === 'undefined' ? undefined : window.innerWidth);
  if (!viewport) return Math.round(Math.max(requested, MIN_READ_ALOUD_MODAL_WIDTH));

  // Below ~580px the gutters would leave less than the minimum width, so fall
  // back to the narrow gutter rather than letting the card run off screen.
  const max = Math.max(
    Math.min(MIN_READ_ALOUD_MODAL_WIDTH, viewport - NARROW_VIEWPORT_GUTTER * 2),
    viewport - VIEWPORT_GUTTER * 2,
  );

  return Math.round(
    Math.min(Math.max(requested, Math.min(MIN_READ_ALOUD_MODAL_WIDTH, max)), max),
  );
}

/**
 * The remembered width, clamped to the *current* viewport — a width dragged out
 * on a desktop monitor shouldn't run off the edge of a laptop screen. The
 * stored value is left alone so the wide setting comes back on the big screen.
 */
export function readStoredReadAloudModalWidth(): number {
  if (typeof window === 'undefined') return DEFAULT_READ_ALOUD_MODAL_WIDTH;

  try {
    const raw = window.localStorage.getItem(READ_ALOUD_MODAL_WIDTH_KEY);
    const parsed = raw === null ? NaN : Number(raw);
    if (!Number.isFinite(parsed))
      return clampReadAloudModalWidth(DEFAULT_READ_ALOUD_MODAL_WIDTH);
    return clampReadAloudModalWidth(parsed);
  } catch {
    // Unparseable or blocked storage.
    return clampReadAloudModalWidth(DEFAULT_READ_ALOUD_MODAL_WIDTH);
  }
}

export function writeStoredReadAloudModalWidth(width: number) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(READ_ALOUD_MODAL_WIDTH_KEY, String(Math.round(width)));
  } catch {
    // Storage full or blocked (private mode) — resizing still works, it just
    // won't be remembered next time.
  }
}
